// Input validation and security checks for AgentBill SDK

export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

/**
 * Validates API key format
 * Must be non-empty alphanumeric with hyphens/underscores, 20-100 chars
 */
export function validateApiKey(apiKey: string): void {
  if (!apiKey || typeof apiKey !== 'string') {
    throw new ValidationError('API key is required and must be a string');
  }

  if (apiKey.length < 20 || apiKey.length > 100) {
    throw new ValidationError('API key must be between 20 and 100 characters');
  }

  if (!/^[a-zA-Z0-9_-]+$/.test(apiKey)) {
    throw new ValidationError('API key contains invalid characters (only alphanumeric, hyphens, and underscores allowed)');
  }
}

/**
 * Validates and sanitizes base URL
 * Prevents SSRF attacks by only allowing HTTPS and specific domains
 */
export function validateBaseUrl(url: string): string {
  if (!url || typeof url !== 'string') {
    throw new ValidationError('Base URL is required and must be a string');
  }

  // Remove trailing slashes
  url = url.replace(/\/+$/, '');

  // Validate URL format
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(url);
  } catch {
    throw new ValidationError('Invalid base URL format');
  }

  // Only allow HTTPS (or HTTP for localhost in development)
  if (parsedUrl.protocol !== 'https:' && 
      parsedUrl.protocol !== 'http:' && 
      !parsedUrl.hostname.includes('localhost') &&
      !parsedUrl.hostname.includes('127.0.0.1')) {
    throw new ValidationError('Base URL must use HTTPS protocol');
  }

  // Prevent SSRF to internal networks
  const hostname = parsedUrl.hostname.toLowerCase();
  const blockedPatterns = [
    /^10\./,          // 10.0.0.0/8
    /^172\.(1[6-9]|2[0-9]|3[0-1])\./, // 172.16.0.0/12
    /^192\.168\./,    // 192.168.0.0/16
    /^169\.254\./,    // 169.254.0.0/16 (link-local)
    /^127\./,         // 127.0.0.0/8 (loopback)
    /^0\./,           // 0.0.0.0/8
    /^localhost$/i,
    /^metadata/i,     // Cloud metadata endpoints
    /^169\.254\.169\.254$/, // AWS/GCP metadata
  ];

  // Allow localhost only in development
  if (!url.includes('localhost') && !url.includes('127.0.0.1')) {
    for (const pattern of blockedPatterns) {
      if (pattern.test(hostname)) {
        throw new ValidationError('Base URL points to internal/private network');
      }
    }
  }

  return url;
}

/**
 * Validates customer ID
 * Must be alphanumeric with limited special chars, max 255 chars
 */
export function validateCustomerId(customerId: string): void {
  if (typeof customerId !== 'string') {
    throw new ValidationError('Customer ID must be a string');
  }

  if (customerId.length === 0 || customerId.length > 255) {
    throw new ValidationError('Customer ID must be between 1 and 255 characters');
  }

  // Allow alphanumeric, hyphens, underscores, dots
  if (!/^[a-zA-Z0-9_.-]+$/.test(customerId)) {
    throw new ValidationError('Customer ID contains invalid characters');
  }
}

/**
 * Validates event name for custom signals
 * Must be alphanumeric with underscores, max 100 chars
 */
export function validateEventName(eventName: string): void {
  if (!eventName || typeof eventName !== 'string') {
    throw new ValidationError('Event name is required and must be a string');
  }

  if (eventName.length === 0 || eventName.length > 100) {
    throw new ValidationError('Event name must be between 1 and 100 characters');
  }

  // Snake_case convention
  if (!/^[a-z0-9_]+$/.test(eventName)) {
    throw new ValidationError('Event name must be lowercase alphanumeric with underscores');
  }
}

/**
 * Validates metadata object
 * Prevents injection and limits size
 */
export function validateMetadata(metadata: Record<string, any>): void {
  if (typeof metadata !== 'object' || metadata === null || Array.isArray(metadata)) {
    throw new ValidationError('Metadata must be a plain object');
  }

  // Limit number of keys
  const keys = Object.keys(metadata);
  if (keys.length > 50) {
    throw new ValidationError('Metadata cannot have more than 50 keys');
  }

  // Validate each key-value pair
  for (const [key, value] of Object.entries(metadata)) {
    // Key validation
    if (typeof key !== 'string' || key.length === 0 || key.length > 100) {
      throw new ValidationError('Metadata keys must be strings between 1 and 100 characters');
    }

    // Value validation (limit size)
    const valueStr = JSON.stringify(value);
    if (valueStr.length > 10000) {
      throw new ValidationError(`Metadata value for key "${key}" exceeds 10KB limit`);
    }
  }

  // Check total size
  const totalSize = JSON.stringify(metadata).length;
  if (totalSize > 100000) {
    throw new ValidationError('Total metadata size exceeds 100KB limit');
  }
}

/**
 * Validates revenue amount
 */
export function validateRevenue(revenue: number): void {
  if (typeof revenue !== 'number' || isNaN(revenue)) {
    throw new ValidationError('Revenue must be a valid number');
  }

  if (revenue < 0) {
    throw new ValidationError('Revenue cannot be negative');
  }

  if (!isFinite(revenue)) {
    throw new ValidationError('Revenue must be a finite number');
  }
}
